import {css, Theme} from "@emotion/react";

import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation";
import {borderRadius, display, flex, flexAbsoluteCenter, mh, onDesktop, p, pb, pl, pr} from "@pg-design/helpers-css";
import {Image} from "@pg-design/image-module";
import {Picture} from "@pg-design/picture-module";
import {Highlight, Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {priceFormat} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {OfferType} from "../../../../types/OfferType";
import {GTMEventAction, hitGtmArchitectCtaEvent} from "../../../tracking/gtm_architect_cta_event";

const architectBannerDesktopImage = require("../../../../images/mid_listing_banners/architect_banner_desktop.jpg");
const architectBannerDesktopXlImage = require("../../../../images/mid_listing_banners/architect_banner_desktop_xl.jpg");
const architectBannerMobileImage = require("../../../../images/mid_listing_banners/architect_banner_mobile.jpg");

interface IProps {
    id: string;
    regionSlug: string;
    minPriceM2?: string;
    offerType?: OfferType;
    className?: string;
}

export const MidListingArchitectBanner = (props: IProps) => {
    const bannerUrl = rpAppLink.architect.city({regionSlug: props.regionSlug});

    const onBtnClick = () => {
        hitGtmArchitectCtaEvent(GTMEventAction.CLICK);
    };

    const {isMobile} = useUserDevice();
    const minPriceM2 = props.minPriceM2 && parseFloat(props.minPriceM2);

    return (
        <a onClick={onBtnClick} href={bannerUrl} target="_blank" css={anchorStyle}>
            <div data-id={props.id} css={wrapperStyle}>
                <div css={textColumnStyle}>
                    <Picture
                        css={[desktopArchitectBannerImgStyle, noInteractionStyle]}
                        sources={[
                            {minWidthPX: 1024, src: architectBannerDesktopImage, height: 140, width: 576},
                            {minWidthPX: 1400, src: architectBannerDesktopXlImage, height: 140, width: 576}
                        ]}
                        alt="picture"
                    />
                    <Text as="div" variant={isMobile ? "headline_3" : "headline_1"} css={[fontWeight600, generalTextStyle, onDesktop(pb(0.5))]}>
                        Wykończ {isMobile ? <br /> : ""} {getOfferTypeName(props.offerType)} pod klucz
                    </Text>
                    <Text as="div" variant={isMobile ? "headline_5" : "headline_2"} css={[fontWeight400, generalTextStyle, pb(0.5), onDesktop(pb(1))]}>
                        Z gwarancją <span css={fontWeight600}>ceny i terminu</span>
                    </Text>
                    <Text as="div" variant={isMobile ? "headline_5" : "headline_2"} css={[fontWeight400, generalTextStyle]}>
                        {minPriceM2 ? (
                            <>
                                od{" "}
                                <Highlight css={fontWeight600}>
                                    <Text as="span" variant={isMobile ? "headline_2" : "headline_1"}>
                                        {priceFormat(minPriceM2)}/
                                    </Text>
                                    m2
                                </Highlight>
                            </>
                        ) : null}
                    </Text>
                </div>
                <div css={buttonColumnStyle}>
                    <Image src={architectBannerMobileImage} width="200px" height="126px" alt="" css={[mobileArchitectBannerImgStyle, noInteractionStyle]} />
                    <Button size={isMobile ? "x-small" : "medium"} variant="banner_primary" css={buttonStyle}>
                        <Text as="span" variant={isMobile ? "headline_6" : "headline_3"} css={fontWeight600}>
                            Sprawdź
                        </Text>
                    </Button>
                </div>
            </div>
        </a>
    );
};

const getOfferTypeName = (offerType?: OfferType) => {
    switch (offerType) {
        case OfferType.FLAT:
            return "mieszkanie";
        case OfferType.HOUSE:
            return "dom";
        default:
            return "nieruchomość";
    }
};

const anchorStyle = (theme: Theme) => css`
    &:hover,
    &:focus,
    &:active {
        color: ${theme.colors.secondary};
    }
`;

const wrapperStyle = (theme: Theme) => css`
    ${p(2)};
    ${mh("auto")};
    ${elevation(2)};
    height: 24rem;
    width: 100%;
    max-width: 37.5rem;
    overflow: hidden;
    background-color: #f9faf4;
    position: relative;
    display: flex;
    justify-content: space-between;
    flex-direction: column;

    @media (min-width: ${theme.breakpoints.md}) {
        ${p(3, 5)};
        ${borderRadius(2)};
        height: 22.5rem;
        max-width: 100%;
        flex-direction: row;
        align-items: center;
    }
`;

const textColumnStyle = css`
    position: relative;
    flex: 0 0 auto;

    ${onDesktop(css`
        flex: 1 1 100%;
    `)};

    @media (min-width: 1400px) {
        max-width: 98rem;
    }
`;

const buttonColumnStyle = css`
    flex: 1 1 100%;
    ${flex("flex-end")};

    ${onDesktop(css`
        display: block;
        ${pl(4)};
        flex: 0 0 auto;
    `)};

    @media (min-width: 1400px) {
        ${flexAbsoluteCenter};
        flex: 1 1 auto;
    }
`;

const generalTextStyle = css`
    ${pr(2)};
    position: relative;
    z-index: 2;
    background-color: rgba(249, 250, 244, 0.65);
    width: fit-content;
    ${borderRadius(2)};
`;

const noInteractionStyle = css`
    user-select: none;
    pointer-events: none;
`;

const desktopArchitectBannerImgStyle = css`
    display: none;

    ${onDesktop(css`
        display: block;
        height: 140px;
        width: 576px;
        position: absolute;
        right: 0;
    `)};
`;

const mobileArchitectBannerImgStyle = css`
    position: absolute;
    bottom: -5px;
    right: 0;

    ${onDesktop(display("none"))};
`;

const buttonStyle = css`
    width: 13rem;

    ${onDesktop(css`
        width: 23rem;
    `)};
`;

const fontWeight400 = css`
    font-weight: 400;
`;

const fontWeight600 = css`
    font-weight: 600;
`;
